import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { PERMISSIONS } from '../utils/permissions';
import {
  FiBell as Bell,
  FiSun as Sun,
  FiMoon as Moon,
  FiHome as Home,
  FiLink as LinkIcon,
  FiCloud as Cloud,
  FiSettings as Cog,
  FiBarChart2 as BarChart3,
  FiSettings as Settings,
  FiDatabase as Database,
  FiActivity as Activity,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import ClientSelector from './ClientSelector';
import GlobalFilterSelector from './GlobalFilterSelector';
import NotificationPanel from './NotificationPanel';
import TrialBanner from './TrialBanner';
import UpgradeBanner from './UpgradeBanner';
import UpgradeModal from './UpgradeModal';

interface NavItem {
  id: string;
  path: string;
  label: string;
  icon: IconType;
}

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { unreadCount, togglePanel } = useNotifications();
  const { hasPermission, isAdmin } = usePermissions();
  const { canAccessModule, isAWSUser } = useModuleAccess();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Show upgrade modal automatically for anonymous sessions (once per session)
  React.useEffect(() => {
    if (user?.is_anonymous) {
      const hasSeenModal = sessionStorage.getItem('upgrade_modal_shown');
      if (!hasSeenModal) {
        setShowUpgradeModal(true);
        sessionStorage.setItem('upgrade_modal_shown', 'true');
      }
    }
  }, [user?.is_anonymous]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Navigation items based on permissions and module access
  const navigationItems = useMemo((): NavItem[] => {
    if (!user) return [];

    // Anonymous users - show all modules
    if (user.is_anonymous) {
      return [
        { id: 'assessments', path: '/hub/assessments', label: 'Assessments', icon: BarChart3 },
        { id: 'hub', path: '/hub', label: 'Hub', icon: Home },
        { id: 'cloudops', path: '/cloudops', label: 'CloudOps', icon: Cloud },
        { id: 'dataops', path: '/dataops', label: 'DataOps', icon: Database },
        { id: 'ai-operations', path: '/ai-operations', label: 'AI Operations', icon: BarChart3 },
      ];
    }

    // System admin / Aivilo staff - show management view
    if (isAdmin || hasPermission(PERMISSIONS.SYSTEM_ADMIN)) {
      return [{ id: 'management', path: '/management', label: 'Management', icon: Cog }];
    }

    const items: NavItem[] = [];

    // Health - permission-based (AWS_ACCESS_*, PARTNERS_*)
    if (
      hasPermission(PERMISSIONS.AWS_ACCESS_VIEW) ||
      hasPermission(PERMISSIONS.AWS_ACCESS_MANAGE) ||
      hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
      hasPermission(PERMISSIONS.PARTNERS_MANAGE)
    ) {
      items.push({ id: 'health', path: '/health', label: 'Health', icon: Activity });
    }

    // Assessment-only users get direct Assessments link before Hub
    if (user.client?.subscription_plan === 'assessment_only') {
      items.push({ id: 'assessments', path: '/hub/assessments', label: 'Assessments', icon: BarChart3 });
    }

    // Hub - accessible to all authenticated users
    items.push({ id: 'hub', path: '/hub', label: 'Hub', icon: Home });

    // CloudOps - check module access for AWS users, permissions for others
    if (isAWSUser()) {
      if (canAccessModule('platformops')) {
        items.push({ id: 'cloudops', path: '/cloudops', label: 'CloudOps', icon: Cloud });
      }
    } else if (
      hasPermission(PERMISSIONS.PLATFORMOPS_MANAGE) ||
      hasPermission(PERMISSIONS.FINANCE_VIEW) ||
      hasPermission(PERMISSIONS.FINANCE_EDIT)
    ) {
      items.push({ id: 'cloudops', path: '/cloudops', label: 'CloudOps', icon: Cloud });
    }

    // DataOps - AWS users use module access, others use permissions
    if (isAWSUser()) {
      if (canAccessModule('dataops')) {
        items.push({ id: 'dataops', path: '/dataops', label: 'DataOps', icon: Database });
      }
    } else if (hasPermission(PERMISSIONS.DATAOPS_MANAGE)) {
      items.push({ id: 'dataops', path: '/dataops', label: 'DataOps', icon: Database });
    }

    // AI Operations (GenAIOps + AgentOps combined)
    if (
      hasPermission(PERMISSIONS.GENAIOPS_MANAGE) ||
      hasPermission(PERMISSIONS.AGENTOPS_MANAGE) ||
      hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
      hasPermission(PERMISSIONS.CLIENTS_MANAGE)
    ) {
      if (!items.find((item) => item.id === 'ai-operations')) {
        items.push({ id: 'ai-operations', path: '/ai-operations', label: 'AI Operations', icon: BarChart3 });
      }
    }

    return items;
  }, [user, hasPermission, isAdmin, canAccessModule, isAWSUser]);

  const getUserNameColor = (): string => {
    if (!user) return 'text-gray-900 dark:text-gray-100';
    switch (user.role) {
      case 'client':
        return 'text-blue-600 dark:text-blue-400';
      case 'partner':
        return 'text-green-600 dark:text-green-400';
      case 'aws':
        return 'text-orange-600 dark:text-orange-400';
      default:
        return 'text-gray-900 dark:text-gray-100';
    }
  };

  const getInitials = (name: string | null): string => {
    const displayName =
      user?.client?.subscription_plan === 'assessment_only' ? user?.client?.name ?? name : name;

    if (!displayName) return user?.email?.substring(0, 2).toUpperCase() ?? 'U';
    const parts = displayName.split(' ').filter((part) => part.length > 0);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const getCompanyName = (): string | null => {
    if (!user) return null;
    if (user.role === 'aws') return 'AWS';
    if (user.role === 'partner' && user.partner_name) return user.partner_name;
    if (user.role === 'client' && user.client?.name) return user.client.name;
    return null;
  };

  const isActive = (path: string): boolean => {
    return location.pathname.startsWith(path);
  };

  // Don't render navigation if user is not loaded yet
  if (!user) {
    return null;
  }

  let displayName: string;
  if (user.is_anonymous) displayName = user.client_name ?? 'Company';
  else if (user.client?.subscription_plan === 'assessment_only') displayName = user.client?.name ?? 'Company';
  else displayName = user.name ?? 'User';

  return (
    <>
      {/* Show UpgradeBanner for anonymous sessions, TrialBanner for assessment_only */}
      {user?.is_anonymous ? (
        <UpgradeBanner onUpgradeClick={() => setShowUpgradeModal(true)} />
      ) : (
        <TrialBanner />
      )}
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex flex-1 min-w-0 overflow-hidden">
              <div className="flex-shrink-0 flex items-center">
                <img
                  src={isDarkMode ? '/lens-logo-dark.png' : '/lens-logo-light.png'}
                  alt="AIVilo Lens"
                  className="h-8 w-auto"
                />
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8 overflow-x-auto nav-scroll-container flex-1 min-w-0 scroll-smooth touch-pan-x">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`${
                        isActive(item.path)
                          ? 'border-blue-500 text-gray-900 dark:text-white'
                          : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-600'
                      } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors flex-shrink-0`}
                    >
                      <Icon className="w-4 h-4 mr-2 flex-shrink-0" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:items-center flex-shrink-0">
              {/* Client Selector for AWS users, partner users, and users with client access permissions */}
              {!location.pathname.startsWith('/health') &&
                (isAWSUser() ||
                  hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
                  hasPermission(PERMISSIONS.PARTNERS_VIEW)) && <ClientSelector />}

              {/* Project Selector - only shown on ops pages (DataOps, CloudOps, GenAIOps, AgentOps) */}
              {!location.pathname.startsWith('/health') &&
                !location.pathname.startsWith('/hub') &&
                !location.pathname.startsWith('/settings') &&
                !location.pathname.startsWith('/integrations') && <GlobalFilterSelector />}

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors mr-4"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Integrations - visible to partners, clients, and users with permission */}
              {(hasPermission(PERMISSIONS.INTEGRATIONS_VIEW) || hasPermission(PERMISSIONS.INTEGRATIONS_MANAGE) || user?.role === 'client' || user?.role === 'partner') && (
                <Link
                  to="/integrations"
                  className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors mr-4"
                  title="Integrations"
                >
                  <LinkIcon className="w-5 h-5" />
                </Link>
              )}

              {/* Notifications Bell */}
              <div className="relative mr-4">
                <button
                  id="notification-bell-button"
                  onClick={togglePanel}
                  className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors"
                  title="Notifications"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Settings - last icon */}
              <Link
                to="/settings"
                className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors mr-2"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </Link>

              <div className="ml-3 relative">
                <div className="flex items-center space-x-3">
                  <span className={`text-sm font-medium ${getUserNameColor()} transition-colors`}>
                    {displayName}
                  </span>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="bg-white dark:bg-gray-700 rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {user.is_anonymous
                          ? user.client_name?.substring(0, 2).toUpperCase() ?? 'CO'
                          : getInitials(user.name)}
                      </span>
                    </div>
                  </button>
                </div>
                {showUserMenu && (
                  <div className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg py-1 bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 focus:outline-none z-50 transition-colors">
                    <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-gray-600">
                      {user.is_anonymous && (
                        <>
                          <div className="font-medium">{user.client_name ?? 'Company'}</div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">Guest Mode</div>
                        </>
                      )}
                      {!user.is_anonymous && user.client?.subscription_plan === 'assessment_only' && (
                        <div className="font-medium">{getCompanyName()}</div>
                      )}
                      {!user.is_anonymous && user.client?.subscription_plan !== 'assessment_only' && (
                        <>
                          <div className="font-medium">{user.name ?? 'User'}</div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">{user.email}</div>
                          {getCompanyName() && (
                            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Company:</span> {getCompanyName()}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {(['client', 'partner'].includes(user.role)) && (
                      <Link
                        to="/settings/subscriptions"
                        onClick={() => setShowUserMenu(false)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        Subscription Settings
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`${
                    isActive(item.path)
                      ? 'bg-blue-50 dark:bg-gray-700 border-blue-500 text-blue-700 dark:text-blue-300'
                      : 'border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-800 dark:hover:text-gray-100'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium w-full text-left transition-colors`}
                >
                  <Icon className="w-4 h-4 inline mr-2" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Notification Panel */}
        <NotificationPanel />
      </nav>

      {/* Upgrade Modal for anonymous sessions */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        companyName={user?.client_name}
      />
    </>
  );
};

export default Navigation;
